import type { LinkProps } from '@hover/blueprint';
import {
  Body,
  Box,
  Button,
  DesignSystemProvider,
  Form,
  Icon,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  TextInput,
  useDisclosure,
} from '@hover/blueprint';
import { useMediaQuery } from '@hover/blueprint/chakra';
import { hClose, iCalendar, iMenu, iPlus, iSearch } from '@hover/icons';
import React, { useState } from 'react';
import { useParams } from 'react-router';

import type { TrackingProps } from 'src/modules/newConstructionUpload/types/newConstructionUploadTypes';
import type { FiltersParams } from 'src/modules/properties/components/Filters/types';
import {
  useFilterParams,
  useSetFilterParams,
} from 'src/modules/properties/components/Filters/useFilterParams';
import {
  createJobFiltersFromUrlSearchParams,
  defaultJobFilters,
} from 'src/modules/properties/utils/filtersUtils';
import { useFeatureAccesses } from 'src/modules/propertyDetails/hooks/useFeatureAccesses';
import { useHasAccessForOrg } from 'src/modules/propertyDetails/hooks/useHasAccessForOrg';
import { useIsHoverAdmin } from 'src/modules/propertyDetails/hooks/useIsHoverAdmin';
import { useIsMobileSize } from 'src/modules/renderings/hooks';
import { MobileNavSheet } from 'src/shared/components/NavBar/MobileNavSheet';
import { SheetNew } from 'src/shared/components/NavBar/SheetNew';
import {
  LOGO_MIN_WIDTH,
  StyledNavBar,
} from 'src/shared/components/NavBar/StyledNavBar';
import { UserIcon } from 'src/shared/components/NavBar/UserIcon';
import { FeatureFlag } from 'src/shared/constants';
import {
  AclTemplateOptions,
  useProfileQuery,
} from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useCurrentPage,
  useTranslate,
  useTreatment,
} from 'src/shared/hooks';
import { useBrand } from 'src/shared/providers/Brand';
import { External, Route } from 'src/shared/route';
import { isFlagEnabled, textInput } from 'src/shared/telemetry';
import { getQueryParamsWithoutSearch } from 'src/shared/utils/url';

export const SHARED_MOBILE_NAV_HEIGHT = 80;

interface NavLinkProps extends LinkProps {
  active?: boolean;
}

const NavMenuItem: React.FC<{ path: string; display: string }> = ({
  path,
  display,
}) => (
  <MenuItem as="a" href={path} paddingY={300} target="self" width="unset">
    {display}
  </MenuItem>
);

const NavLink = ({ ...props }: NavLinkProps) => (
  <Link
    _hover={{ textDecoration: 'underline', textDecorationColor: 'neutral.500' }}
    color="neutral.900"
    fontSize={400}
    justifyContent="center"
    marginRight={`${LOGO_MIN_WIDTH}px`}
    textDecoration={props.active ? 'underline' : 'none'}
    textDecorationColor="neutral.900"
    textUnderlineOffset="1rem"
    whiteSpace="nowrap"
    {...props}
  />
);

export enum NavigationLinks {
  Projects = 'Projects',
  ManageTeam = 'ManageTeam',
}

const NavigationBar = ({ onLogout }: { onLogout: () => void }) => {
  const {
    isOpen: isSheetNewOpen,
    onClose: onSheetNewClose,
    onOpen: onSheetNewOpen,
  } = useDisclosure();
  const isWebUploadEnabled = isFlagEnabled(
    useTreatment(FeatureFlag.Web.WEB_UPLOAD),
  );
  const t = useTranslate(TranslationNamespace.SHARED);
  const { data } = useProfileQuery();
  const {
    isEstimationEnabled,
    isMaterialListEnabled,
    isInspectionChecklistEnabled,
    isAnalyticsPageEnabled,
    isXactimateEnabled,
  } = useFeatureAccesses();
  const [isTeamAdmin] = useHasAccessForOrg([AclTemplateOptions.Admin]);
  const [isJobManager] = useHasAccessForOrg([AclTemplateOptions.JobManager]);
  const filterParams = useFilterParams(createJobFiltersFromUrlSearchParams);
  const setFilterParams = useSetFilterParams<FiltersParams>(
    filterParams,
    defaultJobFilters,
  );
  const [isMobileOrTablet] = useMediaQuery('(max-width: 834px)');

  const currentPage = useCurrentPage();
  const isMobileSize = useIsMobileSize();
  const searchTerm = filterParams?.search ?? '';
  const [isMobileNavOpen, setIsMobileNavOpen] = useState<boolean>(false);
  const isHoverAdmin = useIsHoverAdmin();

  const canManageEhiProjects = isTeamAdmin || isJobManager;
  const isHomeowner = data?.profile?.user?.isHomeowner;

  const isPropertiesPage = currentPage === Route.PROPERTIES.page;
  const isManageTeams = currentPage === Route.MANAGE_TEAMS.page;
  const isEhiPage = window.location.href.includes('/ehi/#/');
  const isManageTeamsInvite = window.location.href.includes(
    'manage-teams/invite',
  );

  const { groupId } = useParams<{ groupId: string }>();
  const isGroupMode = !!groupId;

  const shouldShowSearch =
    currentPage !== Route.PROPERTY_DETAILS.page &&
    currentPage !== Route.ORG_ASSIGNMENTS.page &&
    currentPage !== Route.USER_ASSIGNMENTS.page &&
    !isEhiPage &&
    !isManageTeamsInvite;

  const shouldRenderNewButton = isWebUploadEnabled && isPropertiesPage;

  const user = data?.profile?.user;
  const org = user?.orgs[0];

  const { isUpgradeToProEnabled } = useFeatureAccesses();

  const walletBalance = org?.wallet?.totalBalance
    ? org?.wallet?.totalBalance / 100.0
    : 0;

  const { loadingBrand } = useBrand();

  const currentActiveNavLink = () => {
    if (window.location.pathname === Route.PROPERTIES.path)
      return NavigationLinks.Projects;
    if (window.location.pathname === Route.MANAGE_TEAMS.path)
      return NavigationLinks.ManageTeam;
    return null;
  };

  const trackingProps = (): TrackingProps => ({
    user_email: user?.email ?? '',
    org_id: user?.orgs[0].id,
    page_or_screen_name: 'Projects',
    partner_name: (user?.orgs[0].partner?.name as string) ?? '',
    user_id: user?.id,
    platform: isMobileOrTablet ? 'mobile' : 'web',
  });

  const clearSearch = () => {
    let pathWithoutSearchParams;
    if (isManageTeams) {
      pathWithoutSearchParams = `${Route.MANAGE_TEAMS.path}?${
        getQueryParamsWithoutSearch(location.search) ?? ''
      }`;

      window.location.href = pathWithoutSearchParams;
    } else {
      setFilterParams({
        ...filterParams,
        search: undefined,
      });
    }
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();

    const searchValue = (event.currentTarget.elements[0] as HTMLInputElement)
      .value;

    if (searchValue.length < 3) {
      // If the value was deleted through backspace, clear the search
      if (searchValue === '') clearSearch();
      return;
    }

    textInput({
      input_value: searchTerm,
      input_label: 'search',
      ...trackingProps(),
    });

    let pathWithSearchParams;

    if (isManageTeams) {
      const existingParams = getQueryParamsWithoutSearch(location.search);
      pathWithSearchParams = `${
        Route.MANAGE_TEAMS.path
      }?search=${encodeURIComponent(searchValue)}${
        existingParams ? `&${existingParams}` : ''
      }`;
      window.location.href = pathWithSearchParams;
    } else {
      setFilterParams({
        ...filterParams,
        search: searchValue,
      });
    }
  };

  const handleScheduleDemoClick = () => {
    const scheduleDemoLink = new URL(
      'https://hover.to/lp/hover-sdr/web-app-demo',
    );
    const demoParams = {
      utm_source: '7014N000001zRNkQAM',
      firstname: data?.profile?.user?.firstName ?? '',
      lastname: data?.profile?.user?.lastName ?? '',
      email: data?.profile?.user?.email ?? '',
      mobilephone: data?.profile?.user?.mobilePhone ?? '',
      company: data?.profile?.user?.orgs?.[0].name ?? '',
    };
    Object.entries(demoParams).forEach(([key, value]) => {
      scheduleDemoLink.searchParams.append(key, value);
    });
    window.open(scheduleDemoLink.href, '_blank');
  };

  if (loadingBrand) return null;

  const showWorkflows =
    (isMaterialListEnabled ||
      ((isEstimationEnabled || isInspectionChecklistEnabled) &&
        canManageEhiProjects)) &&
    !isXactimateEnabled;

  // Side navigation menu
  const SheetNewMenu = (
    <DesignSystemProvider attach>
      <SheetNew isOpen={isSheetNewOpen} onClose={onSheetNewClose} />
    </DesignSystemProvider>
  );

  if (isMobileSize && isGroupMode) return null;

  return isMobileSize ? (
    <>
      <Box
        alignItems="center"
        as="header"
        backgroundColor="neutral.0"
        height={`${SHARED_MOBILE_NAV_HEIGHT}px`}
        justifyContent="space-between"
        paddingX={200}
        position="sticky"
        top={0}
        zIndex={1}
      >
        <IconButton
          color="neutral"
          fill="minimal"
          icon={iMenu}
          label="Nav"
          onClick={() => setIsMobileNavOpen(true)}
          size="large"
        />

        {shouldRenderNewButton && (
          <Button
            iconBefore={iPlus}
            onClick={onSheetNewOpen}
            variant="secondary"
          >
            {t('navigation.new')}
          </Button>
        )}
        {isManageTeams && (
          <Form onSubmit={handleSubmit} width="100%">
            <TextInput
              {...(searchTerm && searchTerm.length
                ? {
                    elementAfter: (
                      <IconButton
                        color="neutral"
                        fill="minimal"
                        icon={hClose}
                        label="Clear search"
                        onClick={clearSearch}
                        shape="square"
                      />
                    ),
                  }
                : {})}
              alignItems="center"
              backgroundColor="neutral.100"
              border="none"
              defaultValue={searchTerm}
              elementBefore={<Icon fill="minimal" icon={iSearch} />}
              // Always remount if the initialSearchTerm changes. This helps to clear state on reset.
              key={searchTerm}
              placeholder={
                isManageTeams
                  ? 'Search user by name or email'
                  : 'Search for project by address, id, or name'
              }
              width="100%"
            />
          </Form>
        )}
        <MobileNavSheet
          isOpen={isMobileNavOpen}
          setIsOpen={setIsMobileNavOpen}
        />
      </Box>
      {SheetNewMenu}
    </>
  ) : (
    <>
      <StyledNavBar>
        <Box flex={1}>
          <NavLink
            active={currentActiveNavLink() === NavigationLinks.Projects}
            href={Route.PROPERTIES.path}
          >
            {t('navigation.Jobs')}
          </NavLink>
          {isTeamAdmin && (
            <NavLink
              active={currentActiveNavLink() === NavigationLinks.ManageTeam}
              href={Route.MANAGE_TEAMS.path}
            >
              {t('navigation.manageTeam')}
            </NavLink>
          )}
          {isEstimationEnabled && (
            <NavLink href={External.PROJECT_LIST.path}>
              {t('navigation.Estimates')}
            </NavLink>
          )}
          {isAnalyticsPageEnabled && (isTeamAdmin || isJobManager) && (
            <NavLink href={External.ANALYTICS.path}>
              {t('navigation.analytics')}
            </NavLink>
          )}
          {isHoverAdmin && (
            <NavLink href={External.ADMIN.path}>
              {t('navigation.admin')}
            </NavLink>
          )}
        </Box>
        {shouldShowSearch && (
          <Box flex={1}>
            <Form onSubmit={handleSubmit} width="100%">
              <TextInput
                {...(searchTerm && searchTerm.length
                  ? {
                      elementAfter: (
                        <IconButton
                          color="neutral"
                          fill="minimal"
                          icon={hClose}
                          label="Clear search"
                          onClick={clearSearch}
                          shape="square"
                        />
                      ),
                    }
                  : {})}
                alignItems="center"
                backgroundColor="neutral.100"
                border="none"
                defaultValue={searchTerm}
                elementBefore={<Icon fill="minimal" icon={iSearch} />}
                // Always remount if the initialSearchTerm changes. This helps to clear state on reset.
                key={searchTerm}
                placeholder={
                  isManageTeams
                    ? 'Search user by name or email'
                    : 'Search for project by address, id, or name'
                }
                width="100%"
              />
            </Form>
          </Box>
        )}
        <Box alignItems="center" flex={1} justifyContent="flex-end">
          {!isHomeowner && (
            <Button
              iconBefore={iCalendar}
              onClick={handleScheduleDemoClick}
              variant="ghost"
            >
              {t('navigation.ScheduleDemo')}
            </Button>
          )}
          {shouldRenderNewButton && (
            <Button
              iconBefore={iPlus}
              onClick={onSheetNewOpen}
              variant="secondary"
            >
              {t('navigation.new')}
            </Button>
          )}

          <Menu
            trigger={
              <MenuButton fontWeight={300} height="48px" variant="ghost">
                <UserIcon name={user?.name} />
              </MenuButton>
            }
          >
            {isTeamAdmin && (
              <>
                <Box padding={300}>
                  <Body color="neutral.500" marginY={0}>
                    {t('currency', { val: walletBalance })}&nbsp;
                    {t('navigation.credit')}
                  </Body>
                </Box>
                <MenuDivider />
              </>
            )}
            {showWorkflows && (
              <NavMenuItem
                display={t('navigation.manageWorkflows')}
                path={External.MANAGE_WORKFLOWS.path}
              />
            )}
            {!isHomeowner && (
              <>
                <NavMenuItem
                  display={t('navigation.settings')}
                  path={External.SETTINGS.path}
                />
                <MenuDivider />
              </>
            )}
            {isTeamAdmin && (
              <>
                <NavMenuItem
                  display={t('navigation.planAndBilling')}
                  path={External.PLAN.path}
                />
                <MenuDivider />
              </>
            )}
            <MenuItem onClick={onLogout} paddingY={300}>
              {t('logOut')}
            </MenuItem>

            {isHomeowner && isUpgradeToProEnabled && (
              <Box justifyContent="center" paddingY={300}>
                <Box
                  backgroundColor="neutral.100"
                  borderRadius="10px"
                  flexDirection="column"
                  marginX="300"
                  paddingX="300"
                >
                  <Body fontWeight="bodyBold">
                    {t('navigation.professional')}
                  </Body>
                  <Link
                    color="neutral.900"
                    href={Route.SIGN_UP_PROFESSIONAL_UPGRADE.path}
                    marginBottom={300}
                  >
                    {t('navigation.upgrade')}
                  </Link>
                </Box>
              </Box>
            )}
          </Menu>
        </Box>
      </StyledNavBar>
      {SheetNewMenu}
    </>
  );
};

export { NavigationBar };
